
import React, { useState } from 'react';
import { Drone, DroneMission, Job, MissionTemplate, FlightLog, MissionReport } from '../types';
import { analyzeAerialSurvey } from '../services/gemini';
import { Icons } from '../constants';

interface Props {
  drones: Drone[];
  missions: DroneMission[];
  jobs: Job[];
  missionTemplates?: MissionTemplate[];
  flightLogs?: FlightLog[];
  missionReports?: MissionReport[];
  onAddDrone: (drone: Drone) => void;
  onUpdateMission: (mission: DroneMission) => void;
  onCreateMission: (mission: DroneMission) => void;
  onSimulateFlight?: (mission: DroneMission, pilotId: string) => void;
  onGenerateInvoice?: (mission: DroneMission, contactId: string) => void;
}

const DroneManagement: React.FC<Props> = ({ 
  drones, 
  missions, 
  jobs, 
  missionTemplates = [],
  flightLogs = [],
  missionReports = [],
  onAddDrone, 
  onUpdateMission, 
  onCreateMission,
  onSimulateFlight,
  onGenerateInvoice
}) => {
  const [activeTab, setActiveTab] = useState<'fleet' | 'missions'>('missions');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [selectedMissionId, setSelectedMissionId] = useState<string | null>(null);
  const [showNewMissionModal, setShowNewMissionModal] = useState(false);
  const [confirmingMissionAction, setConfirmingMissionAction] = useState<{
    type: 'deny' | 'approve' | 'workflow';
    status: string;
    mission: DroneMission;
    formData?: any;
  } | null>(null);

  // New Mission Form State
  const [newMission, setNewMission] = useState({
    jobId: '',
    droneId: '',
    missionType: missionTemplates[0]?.name || 'Site Survey',
    price: missionTemplates[0]?.basePrice || 149
  });

  const handleMediaUpload = async (e: React.ChangeEvent<HTMLInputElement>, missionId: string) => {
    if (!e.target.files?.[0]) return;
    setIsAnalyzing(true);
    const file = e.target.files[0];
    const reader = new FileReader();
    reader.onloadend = async () => {
      const mission = missions.find(m => m.id === missionId);
      if (mission) {
        if (onSimulateFlight) {
          await onSimulateFlight(mission, 'pilot-123'); // Mock pilot ID
        } else {
          const base64 = (reader.result as string).split(',')[1];
          const analysis = await analyzeAerialSurvey(base64);
          onUpdateMission({
            ...mission,
            status: 'Completed',
            aerialMedia: [...mission.aerialMedia, reader.result as string],
            aiSummary: analysis ? `AI Report: Found ${analysis.treeCount} large trees and ${analysis.hazardCount} potential roof hazards. Health: ${analysis.canopyHealth}. ${analysis.summary}` : "Analysis failed."
          });
        }
      }
      setIsAnalyzing(false);
    };
    reader.readAsDataURL(file);
  };

  const handleConfirmMissionAction = () => {
    if (!confirmingMissionAction) return;
    const { type, mission, formData } = confirmingMissionAction;

    if (type === 'deny') {
      onUpdateMission({ ...mission, status: 'Denied' });
    } else if (type === 'approve') {
      onUpdateMission({
        ...mission,
        droneId: formData.droneId,
        scheduledDate: formData.scheduledDate,
        pilotInstructions: formData.pilotInstructions,
        adminNotes: formData.adminNotes,
        status: 'Planned'
      });
    } else if (type === 'workflow') {
      onUpdateMission({ ...mission, [confirmingMissionAction.status]: true });
    }

    setConfirmingMissionAction(null);
  };

  const handleCreateMissionSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMission.jobId || !newMission.droneId) return;

    const mission: DroneMission = {
      id: Math.random().toString(36).substr(2, 9),
      jobId: newMission.jobId,
      droneId: newMission.droneId,
      status: 'Planned',
      missionType: newMission.missionType,
      aerialMedia: [],
      price: newMission.price
    };

    onCreateMission(mission);
    setShowNewMissionModal(false);
    setNewMission({ jobId: '', droneId: '', missionType: 'Site Survey', price: 149 });
    setSelectedMissionId(mission.id);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex space-x-2 bg-white p-1 rounded-2xl border border-stone-200 shadow-sm w-fit">
          <button 
            onClick={() => setActiveTab('missions')}
            className={`px-6 py-2 rounded-xl font-bold text-sm transition ${activeTab === 'missions' ? 'bg-palm text-white' : 'text-stone-500 hover:bg-stone-50'}`}
          >
            Aerial Missions
          </button>
          <button 
            onClick={() => setActiveTab('fleet')}
            className={`px-6 py-2 rounded-xl font-bold text-sm transition ${activeTab === 'fleet' ? 'bg-palm text-white' : 'text-stone-500 hover:bg-stone-50'}`}
          >
            Drone Fleet
          </button>
        </div>
        {activeTab === 'missions' && (
          <button 
            onClick={() => setShowNewMissionModal(true)}
            className="bg-gecko text-white px-6 py-2 rounded-xl font-bold hover:bg-palm transition-colors flex items-center space-x-2 shadow-lg shadow-gecko/20"
          >
            <span>+ Schedule Mission</span>
          </button>
        )}
      </div>

      {activeTab === 'missions' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 space-y-4">
            <h3 className="font-bold text-lg text-bark">Mission Queue</h3>
            {missions.length === 0 && (
              <div className="text-stone-400 italic p-12 bg-white rounded-3xl border-2 border-dashed border-stone-100 text-center flex flex-col items-center">
                <Icons.Drone />
                <p className="mt-2">No missions scheduled.</p>
                <button 
                  onClick={() => setShowNewMissionModal(true)}
                  className="mt-4 text-palm font-bold hover:underline text-sm"
                >
                  Create your first mission
                </button>
              </div>
            )}
            {missions.map(mission => {
              const job = jobs.find(j => j.id === mission.jobId);
              const isManual = mission.jobId.startsWith('MANUAL-');
              const displayAddress = isManual ? mission.jobId.replace('MANUAL-', '') : job?.address;
              const displayClient = isManual ? 'Manual Request' : (job?.clientName || 'Unknown Client');
              
              return (
                <div 
                  key={mission.id}
                  onClick={() => setSelectedMissionId(mission.id)}
                  className={`p-5 rounded-2xl border cursor-pointer transition-all transform active:scale-[0.98] ${
                    selectedMissionId === mission.id ? 'border-gecko bg-white shadow-xl ring-2 ring-gecko/10' : 'border-stone-200 bg-white/50 hover:bg-white hover:border-stone-300'
                  }`}
                >
                  <div className="flex justify-between items-center mb-3">
                    <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider ${
                      mission.status === 'Completed' ? 'bg-green-100 text-green-700' : 
                      mission.status === 'Denied' ? 'bg-red-100 text-red-700' :
                      mission.status === 'Pending Approval' ? 'bg-orange-100 text-orange-700' : 
                      'bg-blue-100 text-blue-700'
                    }`}>{mission.status}</span>
                    <span className="text-[10px] font-bold text-stone-400 uppercase tracking-widest">{mission.missionType}</span>
                  </div>
                  <p className="font-bold text-bark text-lg leading-tight">{displayClient}</p>
                  <p className="text-xs text-stone-500 mt-1 truncate">{displayAddress}</p>
                  <div className="mt-4 flex items-center space-x-2">
                    <div className="w-6 h-6 bg-stone-100 rounded-lg flex items-center justify-center text-stone-400">
                       <Icons.Drone />
                    </div>
                    <span className="text-[10px] font-bold text-stone-400 uppercase tracking-widest">
                      Assigned: {drones.find(d => d.id === mission.droneId)?.model || 'Default'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="lg:col-span-2 bg-white rounded-3xl border border-stone-200 shadow-sm p-8 min-h-[500px]">
            {selectedMissionId ? (
              <div className="space-y-8 animate-fade-in">
                {missions.find(m => m.id === selectedMissionId)?.status === 'Pending Approval' && (
                  <div className="bg-stone-50 p-8 rounded-[40px] border-2 border-dashed border-stone-200">
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center space-x-4">
                        <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center text-orange-400 shadow-lg shadow-stone-200/50">
                          <Icons.Drone />
                        </div>
                        <div>
                          <h4 className="text-2xl font-bold text-bark">Client Request Pending</h4>
                          <p className="text-stone-500">Review flight parameters and approve mission.</p>
                        </div>
                      </div>
                      <button 
                        onClick={() => {
                          const mission = missions.find(m => m.id === selectedMissionId);
                          if (mission) setConfirmingMissionAction({ type: 'deny', status: 'Denied', mission });
                        }}
                        className="px-6 py-2 bg-red-50 text-red-600 font-bold rounded-xl hover:bg-red-100 transition"
                      >
                        Deny Request
                      </button>
                    </div>
                    
                    {/* Display Customer Configuration */}
                    <div className="bg-white p-6 rounded-3xl border border-stone-200 mb-8 space-y-4">
                      <h5 className="font-black text-xs text-stone-400 uppercase tracking-widest">Requested Configuration</h5>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div><span className="text-stone-500">Altitude:</span> <span className="font-bold text-bark">{missions.find(m => m.id === selectedMissionId)?.flightAltitude || 100} ft</span></div>
                        <div><span className="text-stone-500">Resolution:</span> <span className="font-bold text-bark">{missions.find(m => m.id === selectedMissionId)?.resolution || 'High'}</span></div>
                        <div><span className="text-stone-500">Price:</span> <span className="font-bold text-palm">${missions.find(m => m.id === selectedMissionId)?.price || '--'}</span></div>
                        <div className="col-span-2"><span className="text-stone-500">Deliverables:</span> <span className="font-bold text-bark">{missions.find(m => m.id === selectedMissionId)?.deliverables?.join(', ') || 'Raw Video'}</span></div>
                        {missions.find(m => m.id === selectedMissionId)?.customerNotes && (
                          <div className="col-span-2"><span className="text-stone-500">Notes:</span> <p className="font-medium text-bark mt-1 p-3 bg-stone-50 rounded-xl">{missions.find(m => m.id === selectedMissionId)?.customerNotes}</p></div>
                        )}
                      </div>
                    </div>

                    {/* Admin Approval Form */}
                    <form onSubmit={(e) => {
                      e.preventDefault();
                      const mission = missions.find(m => m.id === selectedMissionId);
                      const formDataObj = new FormData(e.currentTarget);
                      if (mission && formDataObj.get('droneId')) {
                        setConfirmingMissionAction({
                          type: 'approve',
                          status: 'Planned',
                          mission,
                          formData: {
                            droneId: formDataObj.get('droneId') as string,
                            scheduledDate: formDataObj.get('scheduledDate') as string,
                            pilotInstructions: formDataObj.get('pilotInstructions') as string,
                            adminNotes: formDataObj.get('adminNotes') as string,
                          }
                        });
                      }
                    }} className="space-y-6 text-left">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-xs font-black text-stone-400 uppercase tracking-widest mb-2">Assign Drone</label>
                          <select name="droneId" required className="w-full p-4 bg-white rounded-2xl border border-stone-200 focus:ring-2 focus:ring-gecko outline-none transition font-medium">
                            <option value="">Select available drone...</option>
                            {drones.map(drone => (
                              <option key={drone.id} value={drone.id}>{drone.model} ({drone.status})</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-black text-stone-400 uppercase tracking-widest mb-2">Schedule Date</label>
                          <input type="date" name="scheduledDate" required className="w-full p-4 bg-white rounded-2xl border border-stone-200 focus:ring-2 focus:ring-gecko outline-none transition font-medium" />
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-black text-stone-400 uppercase tracking-widest mb-2">Pilot Instructions (Sent to Pilot)</label>
                        <textarea name="pilotInstructions" rows={2} className="w-full p-4 bg-white rounded-2xl border border-stone-200 focus:ring-2 focus:ring-gecko outline-none transition font-medium resize-none" placeholder="Specific flight path, hazards to avoid..."></textarea>
                      </div>

                      <div>
                        <label className="block text-xs font-black text-stone-400 uppercase tracking-widest mb-2">Internal Admin Notes</label>
                        <textarea name="adminNotes" rows={2} className="w-full p-4 bg-white rounded-2xl border border-stone-200 focus:ring-2 focus:ring-gecko outline-none transition font-medium resize-none" placeholder="Billing notes, special considerations..."></textarea>
                      </div>

                      <button type="submit" className="w-full py-4 bg-gecko text-white font-bold rounded-2xl hover:bg-palm transition shadow-xl shadow-gecko/20">
                        Approve & Dispatch Mission
                      </button>
                    </form>
                  </div>
                )}

                {missions.find(m => m.id === selectedMissionId)?.status === 'Planned' && (
                  <div className="bg-stone-50 p-8 rounded-[40px] border-2 border-dashed border-stone-200">
                    <div className="flex items-center justify-between mb-8">
                      <div className="flex items-center space-x-4">
                        <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center text-palm shadow-lg shadow-stone-200/50">
                          <Icons.Drone />
                        </div>
                        <div>
                          <h4 className="text-2xl font-bold text-bark">Mission Dispatched</h4>
                          <p className="text-stone-500">Scheduled for {missions.find(m => m.id === selectedMissionId)?.scheduledDate || 'TBD'}</p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white p-6 rounded-3xl border border-stone-200 mb-8 space-y-4 text-left">
                      <h5 className="font-black text-xs text-stone-400 uppercase tracking-widest">Pilot Briefing</h5>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div><span className="text-stone-500">Altitude:</span> <span className="font-bold text-bark">{missions.find(m => m.id === selectedMissionId)?.flightAltitude || 100} ft</span></div>
                        <div><span className="text-stone-500">Resolution:</span> <span className="font-bold text-bark">{missions.find(m => m.id === selectedMissionId)?.resolution || 'High'}</span></div>
                        <div><span className="text-stone-500">Price:</span> <span className="font-bold text-palm">${missions.find(m => m.id === selectedMissionId)?.price || '--'}</span></div>
                        <div className="col-span-2"><span className="text-stone-500">Deliverables:</span> <span className="font-bold text-bark">{missions.find(m => m.id === selectedMissionId)?.deliverables?.join(', ') || 'Raw Video'}</span></div>
                        {missions.find(m => m.id === selectedMissionId)?.pilotInstructions && (
                          <div className="col-span-2"><span className="text-stone-500">Instructions:</span> <p className="font-medium text-bark mt-1 p-3 bg-stone-50 rounded-xl">{missions.find(m => m.id === selectedMissionId)?.pilotInstructions}</p></div>
                        )}
                      </div>
                    </div>

                    <div className="text-center">
                      <p className="text-stone-500 mb-6 max-w-sm mx-auto">Upload aerial imagery from the flight to trigger Gemini AI hazard analysis and health assessment.</p>
                      <label className="inline-flex bg-palm text-white px-10 py-4 rounded-2xl font-bold cursor-pointer hover:bg-gecko transition shadow-xl shadow-palm/20 active:scale-95">
                        {isAnalyzing ? (
                           <span className="flex items-center">
                             <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                               <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                               <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                             </svg>
                             Gemini is Analyzing...
                           </span>
                        ) : 'Upload Flight Media'}
                        <input type="file" className="hidden" accept="image/*" onChange={(e) => handleMediaUpload(e, selectedMissionId)} disabled={isAnalyzing} />
                      </label>
                    </div>
                  </div>
                )}

                {missions.find(m => m.id === selectedMissionId)?.status === 'Denied' && (
                  <div className="bg-red-50 p-12 rounded-[40px] border-2 border-dashed border-red-200 text-center">
                    <div className="w-20 h-20 bg-white rounded-3xl flex items-center justify-center mx-auto mb-6 text-red-500 shadow-lg shadow-red-200/50">
                      <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </div>
                    <h4 className="text-2xl font-bold text-red-900 mb-2">Mission Request Denied</h4>
                    <p className="text-red-700/70 mb-8 max-w-sm mx-auto">This flight request was denied by administration.</p>
                  </div>
                )}

                {missions.find(m => m.id === selectedMissionId)?.status === 'Completed' && (
                  <div className="space-y-8">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="text-2xl font-bold text-bark">Mission Intelligence Report</h4>
                        <p className="text-stone-400 text-sm mt-1">Generated by GreenTree AI Architecture</p>
                      </div>
                      <span className="bg-leaf/20 text-palm px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest border border-palm/10">AI Verified</span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {missions.find(m => m.id === selectedMissionId)?.aerialMedia.map((media, i) => (
                        <div key={i} className="group relative rounded-[32px] overflow-hidden border border-stone-200 shadow-lg">
                          <img src={media} alt="Aerial View" className="w-full h-64 object-cover transform group-hover:scale-105 transition duration-700" />
                          <div className="absolute inset-0 bg-gradient-to-t from-bark/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      ))}
                    </div>

                    <div className="bg-bark p-8 rounded-[32px] text-white shadow-2xl relative overflow-hidden">
                      <div className="absolute top-0 right-0 p-8 opacity-10 pointer-events-none">
                        <Icons.Tree />
                      </div>
                      <p className="text-[10px] font-black text-gecko uppercase tracking-[0.2em] mb-4">Arboriculture Intelligence Summary</p>
                      <p className="text-lg leading-relaxed text-stone-200 font-medium italic mb-6">
                        "{missions.find(m => m.id === selectedMissionId)?.aiSummary}"
                      </p>
                      
                      {flightLogs.find(l => l.missionId === selectedMissionId) && (
                        <div className="mt-6 pt-6 border-t border-white/10 grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div>
                            <div className="text-[10px] text-stone-400 uppercase tracking-widest mb-1">Flight Duration</div>
                            <div className="font-bold text-white">{flightLogs.find(l => l.missionId === selectedMissionId)?.durationMins} mins</div>
                          </div>
                          <div>
                            <div className="text-[10px] text-stone-400 uppercase tracking-widest mb-1">Max Altitude</div>
                            <div className="font-bold text-white">{flightLogs.find(l => l.missionId === selectedMissionId)?.maxAltitude} ft</div>
                          </div>
                          <div>
                            <div className="text-[10px] text-stone-400 uppercase tracking-widest mb-1">Battery Usage</div>
                            <div className="font-bold text-white">{flightLogs.find(l => l.missionId === selectedMissionId)?.batteryUsagePercent}%</div>
                          </div>
                          <div>
                            <div className="text-[10px] text-stone-400 uppercase tracking-widest mb-1">Weather</div>
                            <div className="font-bold text-white text-sm">{flightLogs.find(l => l.missionId === selectedMissionId)?.weatherConditions}</div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Post-Flight Workflow */}
                    <div className="flex flex-wrap gap-3 mt-8 pt-8 border-t border-stone-200">
                      <button 
                        onClick={() => {
                          const mission = missions.find(m => m.id === selectedMissionId);
                          if (mission) setConfirmingMissionAction({ type: 'workflow', status: 'reportGenerated', mission });
                        }}
                        className={`px-4 py-2 rounded-xl font-bold text-sm transition ${missions.find(m => m.id === selectedMissionId)?.reportGenerated ? 'bg-gecko text-white' : 'bg-stone-100 text-stone-500 hover:bg-stone-200'}`}
                      >
                        1. Generate Report
                      </button>
                      <button 
                        onClick={() => {
                          const mission = missions.find(m => m.id === selectedMissionId);
                          if (mission) setConfirmingMissionAction({ type: 'workflow', status: 'conferenceScheduled', mission });
                        }}
                        disabled={!missions.find(m => m.id === selectedMissionId)?.reportGenerated}
                        className={`px-4 py-2 rounded-xl font-bold text-sm transition ${missions.find(m => m.id === selectedMissionId)?.conferenceScheduled ? 'bg-gecko text-white' : 'bg-stone-100 text-stone-500 hover:bg-stone-200 disabled:opacity-50'}`}
                      >
                        2. Schedule Conference
                      </button>
                      <button 
                        onClick={() => {
                          const mission = missions.find(m => m.id === selectedMissionId);
                          if (mission) setConfirmingMissionAction({ type: 'workflow', status: 'invoiced', mission });
                        }}
                        disabled={!missions.find(m => m.id === selectedMissionId)?.conferenceScheduled}
                        className={`px-4 py-2 rounded-xl font-bold text-sm transition ${missions.find(m => m.id === selectedMissionId)?.invoiced ? 'bg-gecko text-white' : 'bg-stone-100 text-stone-500 hover:bg-stone-200 disabled:opacity-50'}`}
                      >
                        3. Invoice Client
                      </button>
                      <button 
                        onClick={() => {
                          const mission = missions.find(m => m.id === selectedMissionId);
                          if (mission) setConfirmingMissionAction({ type: 'workflow', status: 'paid', mission });
                        }}
                        disabled={!missions.find(m => m.id === selectedMissionId)?.invoiced}
                        className={`px-4 py-2 rounded-xl font-bold text-sm transition ${missions.find(m => m.id === selectedMissionId)?.paid ? 'bg-gecko text-white' : 'bg-stone-100 text-stone-500 hover:bg-stone-200 disabled:opacity-50'}`}
                      >
                        4. Mark Paid
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-stone-300 opacity-50">
                <div className="w-24 h-24 mb-6">
                   <Icons.Drone />
                </div>
                <p className="font-bold uppercase tracking-[0.3em] text-sm">Select mission to view telemetry</p>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'fleet' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {drones.map(drone => (
            <div key={drone.id} className="bg-white p-8 rounded-3xl border border-stone-200 shadow-sm relative overflow-hidden group">
              <div className="flex justify-between items-start mb-6 relative z-10">
                <div>
                  <h4 className="font-bold text-xl text-bark group-hover:text-palm transition-colors">{drone.model}</h4>
                  <p className="text-xs text-stone-400 font-mono mt-1">SN: {drone.serialNumber}</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${
                  drone.status === 'Available' ? 'bg-green-100 text-green-700 border border-green-200' : 'bg-red-100 text-red-700 border border-red-200'
                }`}>
                  {drone.status}
                </span>
              </div>
              <div className="space-y-4 relative z-10">
                <div className="flex justify-between items-center text-sm p-3 bg-stone-50 rounded-xl">
                  <span className="text-stone-500 font-bold uppercase text-[10px]">Command Pilot</span>
                  <span className="font-bold text-bark">{drone.assignedPilot}</span>
                </div>
                <div className="flex justify-between items-center text-sm p-3 bg-stone-50 rounded-xl">
                  <span className="text-stone-500 font-bold uppercase text-[10px]">Airworthiness Check</span>
                  <span className="font-bold text-bark">{drone.lastMaintenance}</span>
                </div>
              </div>
              <div className="absolute -right-4 -bottom-4 opacity-5 text-bark transform group-hover:rotate-12 group-hover:scale-125 transition-transform duration-500">
                <div className="w-32 h-32">
                   <Icons.Drone />
                </div>
              </div>
            </div>
          ))}
          <button 
            onClick={() => onAddDrone({ id: Math.random().toString(), model: 'Mavic 3 Enterprise', serialNumber: 'NEW-SN-' + Date.now(), lastMaintenance: new Date().toISOString(), status: 'Available', assignedPilot: 'Lead Arborist' })}
            className="border-2 border-dashed border-stone-200 rounded-3xl p-8 flex flex-col items-center justify-center text-stone-400 hover:bg-white hover:border-gecko hover:shadow-xl transition-all group min-h-[220px]"
          >
            <div className="w-14 h-14 rounded-2xl bg-stone-50 flex items-center justify-center group-hover:bg-leaf/20 group-hover:text-palm transition-colors">
              <span className="text-3xl font-light">+</span>
            </div>
            <p className="mt-4 font-black text-xs uppercase tracking-widest">Commission Drone</p>
          </button>
        </div>
      )}

      {/* New Mission Modal */}
      {showNewMissionModal && (
        <div className="fixed inset-0 bg-bark/70 backdrop-blur-md flex items-center justify-center z-[150] p-4">
          <div className="bg-white rounded-[40px] w-full max-w-lg shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300">
            <div className="bg-bark p-8 text-white relative">
              <div className="relative z-10">
                <h3 className="text-2xl font-bold">Schedule Aerial Mission</h3>
                <p className="text-stone-400 text-sm mt-1">Configure flight parameters and objectives.</p>
              </div>
              <div className="absolute right-0 top-0 p-8 opacity-10">
                <div className="w-16 h-16">
                  <Icons.Drone />
                </div>
              </div>
            </div>
            <form onSubmit={handleCreateMissionSubmit} className="p-10 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-black text-stone-400 uppercase tracking-widest mb-2">Objective Category</label>
                  <select 
                    required
                    className="w-full p-4 bg-stone-50 rounded-2xl border border-stone-200 focus:ring-2 focus:ring-gecko outline-none transition font-medium"
                    value={newMission.missionType}
                    onChange={e => {
                      const type = e.target.value;
                      const template = missionTemplates.find(t => t.name === type);
                      setNewMission({ 
                        ...newMission, 
                        missionType: type as any,
                        price: template?.basePrice || 149
                      });
                    }}
                  >
                    {missionTemplates.map(template => (
                      <option key={template.name} value={template.name}>{template.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-black text-stone-400 uppercase tracking-widest mb-2">Mission Price ($)</label>
                  <input 
                    type="number"
                    required
                    min="0"
                    className="w-full p-4 bg-stone-50 rounded-2xl border border-stone-200 focus:ring-2 focus:ring-gecko outline-none transition font-medium"
                    value={newMission.price}
                    onChange={e => setNewMission({...newMission, price: Number(e.target.value)})}
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-black text-stone-400 uppercase tracking-widest mb-2">Assign Project Property</label>
                <select 
                  required
                  className="w-full p-4 bg-stone-50 rounded-2xl border border-stone-200 focus:ring-2 focus:ring-gecko outline-none transition font-medium"
                  value={newMission.jobId}
                  onChange={e => setNewMission({...newMission, jobId: e.target.value})}
                >
                  <option value="">Select an active job...</option>
                  {jobs.map(job => (
                    <option key={job.id} value={job.id}>{job.clientName} — {job.address}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-black text-stone-400 uppercase tracking-widest mb-2">Select Unmanned Aircraft</label>
                <select 
                  required
                  className="w-full p-4 bg-stone-50 rounded-2xl border border-stone-200 focus:ring-2 focus:ring-gecko outline-none transition font-medium"
                  value={newMission.droneId}
                  onChange={e => setNewMission({...newMission, droneId: e.target.value})}
                >
                  <option value="">Select available drone...</option>
                  {drones.map(drone => (
                    <option key={drone.id} value={drone.id}>{drone.model} ({drone.status})</option>
                  ))}
                </select>
              </div>

              <div className="flex space-x-4 pt-4">
                <button 
                  type="button" 
                  onClick={() => setShowNewMissionModal(false)} 
                  className="flex-1 py-4 text-stone-400 font-bold hover:bg-stone-50 rounded-2xl transition"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  disabled={!newMission.jobId || !newMission.droneId}
                  className="flex-1 py-4 bg-gecko text-white font-bold rounded-2xl hover:bg-palm transition shadow-xl shadow-gecko/20 disabled:opacity-50 disabled:shadow-none transform active:scale-95"
                >
                  Launch Mission
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Confirmation Modal */}
      {confirmingMissionAction && (
        <div className="fixed inset-0 bg-bark/60 backdrop-blur-sm flex items-center justify-center z-[200] p-4">
          <div className="bg-white rounded-[32px] w-full max-w-md shadow-2xl p-8 text-center animate-in fade-in zoom-in duration-300">
            <div className="w-16 h-16 bg-palm/10 text-palm rounded-full flex items-center justify-center mx-auto mb-6">
              <Icons.Drone />
            </div>
            <h3 className="text-2xl font-bold text-bark mb-2">Confirm Mission Action</h3>
            <p className="text-stone-500 mb-8 leading-relaxed">
              You are about to {confirmingMissionAction.type === 'deny' ? 'deny' : confirmingMissionAction.type === 'approve' ? 'approve and dispatch' : 'update'} this mission. 
              This will notify the relevant personnel and update the flight schedule.
            </p>
            <div className="flex space-x-3">
              <button 
                onClick={() => setConfirmingMissionAction(null)}
                className="flex-1 py-4 border-2 border-stone-100 text-stone-500 font-bold rounded-2xl hover:bg-stone-50 transition"
              >
                Cancel
              </button>
              <button 
                onClick={handleConfirmMissionAction}
                className="flex-1 py-4 bg-palm text-white font-bold rounded-2xl hover:bg-bark transition shadow-xl shadow-palm/20"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DroneManagement;
